import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { Get } from "../config/api/get";
import { Post } from "../config/api/post";
import { useSelector } from "react-redux";
import { ACTIVITY, CONTENT_TYPE } from "../config/constants";

const ACTIVITY_KEY = ["activities"];

const invalidateActivities = (queryClient, id) => {
  queryClient.invalidateQueries({ queryKey: ACTIVITY_KEY });
  if (id) queryClient.invalidateQueries({ queryKey: ["activity", id] });
};

export const useGetAllActivities = (params = {}) => {
  const token = useSelector((state) => state.user.userToken);
  return useQuery({
    queryKey: [...ACTIVITY_KEY, params],
    queryFn: async ({ signal }) => {
      const { data } = await Get(ACTIVITY.getAllActivities, token, params, { signal });
      return data || { docs: [], totalPages: 1, totalDocs: 0 };
    },
    enabled: !!token,
    placeholderData: keepPreviousData,
  });
};

export const useGetActivityById = (id) => {
  const token = useSelector((state) => state.user.userToken);
  return useQuery({
    queryKey: ["activity", id],
    queryFn: async () => {
      const { data } = await Get(ACTIVITY.getActivityById + id, token);
      return data?.activity;
    },
    enabled: !!token && !!id,
  });
};

export const useAddActivity = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);
  return useMutation({
    mutationFn: async ({ data }) => {
      const response = await Post(ACTIVITY.addActivity, data, token, null, CONTENT_TYPE.FORM_DATA);
      if (response?.data?.status) return response.data;
      throw new Error(response?.data?.message || "Failed to create activity");
    },
    onSuccess: () => invalidateActivities(queryClient),
  });
};

export const useUpdateActivity = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);
  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await Post(ACTIVITY.updateActivity + id, data, token, null, CONTENT_TYPE.FORM_DATA);
      if (response?.data?.status !== false) return response.data;
      throw new Error(response?.data?.message || "Failed to update activity");
    },
    onSuccess: (_data, variables) => invalidateActivities(queryClient, variables?.id),
  });
};

export const useDeleteActivity = () => {
  const queryClient = useQueryClient();
  const token = useSelector((state) => state.user.userToken);
  return useMutation({
    mutationFn: async (id) => {
      const response = await Get(ACTIVITY.deleteActivity + id, token);
      if (response?.status !== false) return response;
      throw new Error(response?.message || "Failed to delete activity");
    },
    onSuccess: () => invalidateActivities(queryClient),
  });
};
